// InlineSelect — a styled, accessible replacement for the native <select>.
//
// The OS <select> popup can't be themed and looks jarring inside our modern
// forms (see the needs edit modal). This renders a quiet field-styled trigger
// plus a floating panel of options with keyboard support. Reusable app-wide —
// needs today, modules/programs next.
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface InlineSelectOption {
    value: string;
    label: string;
    hint?: string;
}

interface InlineSelectProps {
    value: string;
    onChange: (value: string) => void;
    options: InlineSelectOption[];
    placeholder?: string;
    icon?: LucideIcon;
    disabled?: boolean;
    className?: string;
    /** 'quiet' = borderless inline (default); 'outline' = bordered pill. */
    variant?: 'quiet' | 'outline';
    ariaLabel?: string;
}

const TRIGGER_BASE =
    'w-full flex items-center gap-2 text-sm text-left transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const TRIGGER_QUIET =
    'bg-transparent text-gray-900 border-0 border-b border-transparent hover:border-gray-200 focus:border-[#1A4D4F] focus:outline-none px-0 py-1';
const TRIGGER_OUTLINE =
    'bg-white text-gray-900 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent';

export const InlineSelect: React.FC<InlineSelectProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Välj…',
    icon: Icon,
    disabled,
    className,
    variant = 'quiet',
    ariaLabel,
}) => {
    const [open, setOpen] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const rootRef = useRef<HTMLDivElement>(null);

    const selected = options.find((o) => o.value === value);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    // When opening, focus the currently-selected option.
    useEffect(() => {
        if (open) setActiveIdx(options.findIndex((o) => o.value === value));
    }, [open, value, options]);

    const commit = (idx: number) => {
        const opt = options[idx];
        if (!opt) return;
        onChange(opt.value);
        setOpen(false);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (disabled) return;
        if (e.key === 'Escape') { setOpen(false); return; }
        if (!open && (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown')) {
            e.preventDefault(); setOpen(true); return;
        }
        if (!open) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(options.length - 1, i + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
        else if (e.key === 'Enter') { e.preventDefault(); commit(activeIdx); }
    };

    return (
        <div ref={rootRef} className={`relative ${className || ''}`}>
            <button
                type="button"
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
                onClick={() => !disabled && setOpen((v) => !v)}
                onKeyDown={onKeyDown}
                className={`${TRIGGER_BASE} ${variant === 'quiet' ? TRIGGER_QUIET : TRIGGER_OUTLINE}`}
            >
                {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />}
                <span className={`flex-1 min-w-0 truncate ${selected ? '' : 'text-gray-400'}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <ul
                    role="listbox"
                    className="absolute z-50 left-0 right-0 mt-1 max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
                >
                    {options.map((opt, idx) => {
                        const isSel = opt.value === value;
                        const isActive = idx === activeIdx;
                        return (
                            <li key={opt.value || `__${idx}`} role="option" aria-selected={isSel}>
                                <button
                                    type="button"
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    onClick={() => commit(idx)}
                                    className={`w-full flex items-start gap-2 px-3 py-2 text-left text-sm transition-colors ${
                                        isActive ? 'bg-[#A4C3B2]/20' : ''
                                    } ${isSel ? 'text-[#1A4D4F] font-medium' : 'text-gray-700'}`}
                                >
                                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSel ? 'text-[#1A4D4F]' : 'text-transparent'}`} />
                                    <span className="min-w-0">
                                        <span className="block truncate">{opt.label}</span>
                                        {opt.hint && <span className="block text-xs text-gray-400 truncate">{opt.hint}</span>}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default InlineSelect;
